import { api } from './api';

/**
 * Build workspace filter params
 * @param {Object} filterProps - Filter props from WorkspaceContext
 * @returns {Object} Query parameters for workspace filtering
 */
const buildWorkspaceParams = (filterProps = {}) => {
  const params = {};
  if (filterProps.organizationId) {
    params.organizationId = filterProps.organizationId;
  }
  if (filterProps.teamId) {
    params.teamId = filterProps.teamId;
  }
  return params;
};

/**
 * Get the methodology and hill status mapping for an organization
 * @param {string} organizationId
 */
export const getShapeUpSettings = async (organizationId) => {
  const res = await api.get('/shape-up/settings', {
    params: buildWorkspaceParams({ organizationId })
  });
  return res.data;
};

/**
 * Update methodology and/or the hill status mapping (org admins only)
 * @param {string} organizationId
 * @param {Object} updates - { methodology, statusPositions, parkedStatuses }
 */
export const updateShapeUpSettings = async (organizationId, updates = {}) => {
  const res = await api.put('/shape-up/settings', { organizationId, ...updates });
  return res.data;
};

/**
 * Every Jira status the team's boards use, and where the hill puts each one
 * @param {string} organizationId
 */
export const getStatusMap = async (organizationId) => {
  const res = await api.get('/shape-up/status-map', {
    params: buildWorkspaceParams({ organizationId })
  });
  return res.data;
};

/**
 * Every Jira issue type the team's projects offer, which ones count as scopes,
 * and what a new scope is created as.
 * @param {string} organizationId
 */
export const getIssueTypeMap = async (organizationId) => {
  const res = await api.get('/shape-up/issue-types', {
    params: buildWorkspaceParams({ organizationId })
  });
  return res.data;
};

/**
 * List cycles for an organization, optionally narrowed to one team
 * @param {Object} filterProps - { organizationId, teamId }
 */
export const getCycles = async (filterProps = {}) => {
  const params = buildWorkspaceParams(filterProps);
  if (filterProps.includeArchived) params.includeArchived = true;
  const res = await api.get('/shape-up/cycles', { params });
  return res.data;
};

/**
 * Create a cycle
 * @param {Object} payload - { organizationId, teamId, name, startDate, endDate, status }
 */
export const createCycle = async (payload) => {
  const res = await api.post('/shape-up/cycles', payload);
  return res.data;
};

/**
 * Update a cycle
 * @param {string} cycleId
 * @param {Object} updates - { name, startDate, endDate, status }
 */
export const updateCycle = async (cycleId, updates) => {
  const res = await api.patch(`/shape-up/cycles/${cycleId}`, updates);
  return res.data;
};

/**
 * Delete a cycle along with its bets and scopes
 * @param {string} cycleId
 */
export const deleteCycle = async (cycleId) => {
  const res = await api.delete(`/shape-up/cycles/${cycleId}`);
  return res.data;
};

/**
 * Archive a cycle: hide it from the active lists and selectors without
 * deleting its bets. Reversible via restoreCycle.
 * @param {string} cycleId
 */
export const archiveCycle = async (cycleId) => {
  const res = await api.post(`/shape-up/cycles/${cycleId}/archive`);
  return res.data;
};

/**
 * Restore a previously archived cycle back into the active lists.
 * @param {string} cycleId
 */
export const restoreCycle = async (cycleId) => {
  const res = await api.post(`/shape-up/cycles/${cycleId}/restore`);
  return res.data;
};

/**
 * Everyone in the organization with what they have actually shipped — the repos
 * their pull requests and commits landed in — and the bets they are carrying.
 * Derived from GitHub evidence, so nothing here is editable.
 * @param {string} organizationId
 */
export const getShapeUpPeople = async (organizationId) => {
  const res = await api.get('/shape-up/people', {
    params: buildWorkspaceParams({ organizationId }),
  });
  return res.data;
};

/**
 * Every tag used in the organization, ordered by how many bets carry it. Feeds
 * the suggestions in the tag box so the vocabulary converges instead of
 * fragmenting into near-duplicates.
 * @param {string} organizationId
 */
export const getShapeUpTags = async (organizationId) => {
  const res = await api.get('/shape-up/tags', {
    params: buildWorkspaceParams({ organizationId }),
  });
  return res.data;
};

/**
 * What a bet is probably about, guessed from its re-bet history, the vocabulary the
 * organization already uses, and the repositories its code landed in. Each guess
 * carries the reason for it. Nothing is written; the caller accepts or discards.
 * @param {string} betId
 */
export const getBetTagSuggestions = async (betId) => {
  const res = await api.get(`/shape-up/bets/${betId}/tag-suggestions`);
  return res.data;
};

/**
 * The bets carrying a tag and the people who worked on them. The reason tags sit
 * on bets: "who has done billing work" is answered from the bets, so it stays
 * true as people move around.
 * @param {string} slug
 * @param {string} organizationId
 */
export const getTagDetail = async (slug, organizationId) => {
  const res = await api.get(`/shape-up/tags/${encodeURIComponent(slug)}`, {
    params: buildWorkspaceParams({ organizationId }),
  });
  return res.data;
};

/**
 * Who is working a cycle, plus everyone in the organization who could be. Both
 * come back together so the picker needs one request.
 * @param {string} cycleId
 */
export const getCycleRoster = async (cycleId) => {
  const res = await api.get(`/shape-up/cycles/${cycleId}/roster`);
  return res.data;
};

/**
 * Set who is working a cycle. Replaces the roster wholesale; anyone dropped is
 * also taken off this cycle's bet teams.
 * @param {string} cycleId
 * @param {Array<{userId: string, role: string}>} members
 */
export const setCycleRoster = async (cycleId, members) => {
  const res = await api.put(`/shape-up/cycles/${cycleId}/roster`, { members });
  return res.data;
};

/**
 * Add another cycle's roster to this one. Additive: anyone already here keeps
 * the role they have.
 * @param {string} cycleId
 * @param {string} fromCycleId
 */
export const copyCycleRoster = async (cycleId, fromCycleId) => {
  const res = await api.post(`/shape-up/cycles/${cycleId}/roster/copy`, { fromCycleId });
  return res.data;
};

/**
 * The team on a bet, plus the cycle roster to pick from.
 * @param {string} betId
 */
export const getBetTeam = async (betId) => {
  const res = await api.get(`/shape-up/bets/${betId}/team`);
  return res.data;
};

/**
 * Set the team on a bet. Everyone named must already be on the cycle's roster.
 * @param {string} betId
 * @param {Array<{userId: string, role: string}>} members
 */
export const setBetTeam = async (betId, members) => {
  const res = await api.put(`/shape-up/bets/${betId}/team`, { members });
  return res.data;
};

/**
 * Give a bet a Slack channel and invite its team, or point it at a channel that
 * already exists. Also the retry path when a channel failed to appear as the bet
 * was placed.
 * @param {string} betId
 * @param {Object} [existing] - { channelId, channelName } to link instead of create
 */
export const createBetSlackChannel = async (betId, existing = {}) => {
  const res = await api.post(`/shape-up/bets/${betId}/slack-channel`, existing);
  return res.data;
};

/**
 * Archive the Slack channels of a cycle's bets. Offered when a cycle closes.
 * @param {string} cycleId
 */
export const archiveCycleSlackChannels = async (cycleId) => {
  const res = await api.post(`/shape-up/cycles/${cycleId}/slack-channels/archive`);
  return res.data;
};

/**
 * Get the hill chart read model for a cycle: every bet with its scopes and
 * each scope's position already resolved from its override or Jira status.
 * @param {string} cycleId
 */
export const getCycleHill = async (cycleId) => {
  const res = await api.get(`/shape-up/cycles/${cycleId}/hill`);
  return res.data;
};

/**
 * Get a single bet with its scopes and cycle, so a shared bet link resolves
 * without the caller knowing which cycle it belongs to.
 * @param {string} betId
 */
export const getBetHill = async (betId) => {
  const res = await api.get(`/shape-up/bets/${betId}/hill`);
  return res.data;
};

/**
 * Create a bet within a cycle
 * @param {string} cycleId
 * @param {Object} payload - { name, description, appetite, jiraEpicKey, jiraEpicSummary, pitchUrl }
 */
export const createBet = async (cycleId, payload) => {
  const res = await api.post(`/shape-up/cycles/${cycleId}/bets`, payload);
  return res.data;
};

/**
 * Update a bet
 * @param {string} betId
 * @param {Object} updates - { name, description, appetite, jiraEpicKey, pitchUrl, order }
 */
export const updateBet = async (betId, updates) => {
  const res = await api.patch(`/shape-up/bets/${betId}`, updates);
  return res.data;
};

/**
 * Delete a bet and its scopes
 * @param {string} betId
 */
export const deleteBet = async (betId) => {
  const res = await api.delete(`/shape-up/bets/${betId}`);
  return res.data;
};

/**
 * Save the caller's personal bet order for a cycle. Personal to this user; does
 * not change the shared order or what teammates see.
 * @param {string} cycleId
 * @param {string[]} orderedBetIds - bet ids in the caller's chosen order
 */
export const setCycleBetOrder = async (cycleId, orderedBetIds) => {
  const res = await api.put(`/shape-up/cycles/${cycleId}/bet-order`, { orderedBetIds });
  return res.data;
};

/**
 * Star or unstar a bet for the caller. Personal to this user.
 * @param {string} betId
 * @param {boolean} isFavorite
 */
export const setBetFavorite = async (betId, isFavorite) => {
  const res = await api.put(`/shape-up/bets/${betId}/favorite`, { isFavorite });
  return res.data;
};

/**
 * Pull every child issue of the bet's linked Jira epic in as scopes.
 * Manual overrides, parked flags, and exclusions are preserved.
 * @param {string} betId
 */
export const syncBetScopes = async (betId) => {
  const res = await api.post(`/shape-up/bets/${betId}/sync`, {});
  return res.data;
};

/**
 * Sync every Jira-linked bet in a cycle in one request. The response carries the
 * oldest bet's sync time, so "synced X ago" honestly covers the whole cycle.
 * @param {string} cycleId
 */
export const syncCycleScopes = async (cycleId) => {
  const res = await api.post(`/shape-up/cycles/${cycleId}/sync`, {});
  return res.data;
};

/**
 * A compact Jira sync summary for the org's current cycle, for the dashboard
 * badge. Returns { jiraEnabled: false } when the workspace has Jira off.
 * @param {string} organizationId
 */
export const getShapeUpSyncStatus = async (organizationId) => {
  const res = await api.get('/shape-up/sync-status', {
    params: buildWorkspaceParams({ organizationId }),
  });
  return res.data;
};

/**
 * Shaped work waiting to be bet on.
 * @param {Object} params - { organizationId, teamId, status }
 */
export const getPitches = async (params = {}) => {
  const res = await api.get('/shape-up/pitches', { params: buildWorkspaceParams(params) });
  return res.data;
};

/**
 * Write down a shaped idea.
 * @param {Object} payload - { organizationId, title, problem, appetite, solution, rabbitHoles, noGos, status }
 */
export const createPitch = async (payload) => {
  const res = await api.post('/shape-up/pitches', payload);
  return res.data;
};

/**
 * Reshape a pitch. Moving it back to ready resets how long it has been waiting.
 * @param {string} pitchId
 * @param {Object} updates
 */
export const updatePitch = async (pitchId, updates) => {
  const res = await api.patch(`/shape-up/pitches/${pitchId}`, updates);
  return res.data;
};

/**
 * Delete a pitch.
 * @param {string} pitchId
 */
export const deletePitch = async (pitchId) => {
  const res = await api.delete(`/shape-up/pitches/${pitchId}`);
  return res.data;
};

/**
 * Bet on a pitch, creating a bet in the chosen cycle.
 * @param {string} pitchId
 * @param {string} cycleId
 * @param {Object} [jira] - { jiraEpicKey, jiraEpicSummary }
 */
export const betOnPitch = async (pitchId, cycleId, jira = {}) => {
  const res = await api.post(`/shape-up/pitches/${pitchId}/bet`, { cycleId, ...jira });
  return res.data;
};

/**
 * Close a cycle, recording how each bet ended.
 * @param {string} cycleId
 * @param {Array<{betId: string, outcome: string, note: string}>} outcomes
 *   Anything left out falls back to what its scopes suggest.
 */
export const closeCycle = async (cycleId, outcomes = []) => {
  const res = await api.post(`/shape-up/cycles/${cycleId}/close`, { outcomes });
  return res.data;
};

/**
 * Reopen a closed cycle, clearing the outcomes that were recorded.
 * @param {string} cycleId
 */
export const reopenCycle = async (cycleId) => {
  const res = await api.post(`/shape-up/cycles/${cycleId}/reopen`, {});
  return res.data;
};

/**
 * Bet on this work again in another cycle. Scopes are not carried over.
 * @param {string} betId
 * @param {string} cycleId - the cycle to bet into
 */
export const rebet = async (betId, cycleId) => {
  const res = await api.post(`/shape-up/bets/${betId}/rebet`, { cycleId });
  return res.data;
};

/**
 * Every recorded position for a bet's scopes, oldest first, for dot trails and
 * the cycle replay.
 * @param {string} betId
 */
export const getBetHistory = async (betId) => {
  const res = await api.get(`/shape-up/bets/${betId}/history`);
  return res.data;
};

/**
 * Analyze scope coverage against the bet's pitch using AI. Returns gaps in coverage,
 * rabbit hole risks, no-go violations, and an overall summary. Results are cached.
 * @param {string} betId
 * @param {boolean} refresh - Force a new analysis (counts against usage limit)
 */
export const getScopeCoverage = async (betId, refresh = false) => {
  const params = refresh ? { refresh: 'true' } : {};
  const res = await api.get(`/shape-up/bets/${betId}/scope-coverage`, { params });
  return res.data;
};

/**
 * Manually attach a Jira issue to a bet as a scope
 * @param {string} betId
 * @param {string} jiraKey
 */
export const addScope = async (betId, jiraKey) => {
  const res = await api.post(`/shape-up/bets/${betId}/scopes`, { jiraKey });
  return res.data;
};

/**
 * Update a scope. Pass hillPositionOverride as null to hand position control
 * back to the Jira status.
 * @param {string} scopeId
 * @param {Object} updates - { hillPositionOverride, isParked, isExcluded }
 */
export const updateScope = async (scopeId, updates) => {
  const res = await api.patch(`/shape-up/scopes/${scopeId}`, updates);
  return res.data;
};

/**
 * Remove a scope from a bet
 * @param {string} scopeId
 */
export const removeScope = async (scopeId) => {
  const res = await api.delete(`/shape-up/scopes/${scopeId}`);
  return res.data;
};

/**
 * Create a Jira issue under the bet's epic and attach it as a scope
 * @param {string} betId
 * @param {Object} payload - { summary, issueType }
 */
export const createScopeIssue = async (betId, payload) => {
  const res = await api.post(`/shape-up/bets/${betId}/scopes/new`, payload);
  return res.data;
};

/**
 * A scope's todos are the Jira subtasks of its issue; nothing is stored here.
 * @param {string} scopeId
 */
export const getScopeTodos = async (scopeId) => {
  const res = await api.get(`/shape-up/scopes/${scopeId}/todos`);
  return res.data;
};

/**
 * Every scope's todos for a bet in one request, keyed by Jira issue key. The
 * board needs all of them at once, and per-scope requests would be one Jira
 * round trip per dot.
 * @param {string} betId
 */
export const getBetTodos = async (betId) => {
  const res = await api.get(`/shape-up/bets/${betId}/todos`);
  return res.data;
};

/**
 * The bet's scratchpad: loose, team-shared tasks. Ordered as the team arranged
 * them. Rides along in the hill payload too; this refetches on demand.
 * @param {string} betId
 */
export const getBetTasks = async (betId) => {
  const res = await api.get(`/shape-up/bets/${betId}/tasks`);
  return res.data;
};

/**
 * Jot a new task onto a bet's scratchpad.
 * @param {string} betId
 * @param {string} title
 */
export const addBetTask = async (betId, title) => {
  const res = await api.post(`/shape-up/bets/${betId}/tasks`, { title });
  return res.data;
};

/**
 * Rename a scratchpad task or check it off.
 * @param {string} taskId
 * @param {Object} updates - { title, done }
 */
export const updateBetTask = async (taskId, updates) => {
  const res = await api.patch(`/shape-up/bet-tasks/${taskId}`, updates);
  return res.data;
};

/**
 * Remove a scratchpad task.
 * @param {string} taskId
 */
export const deleteBetTask = async (taskId) => {
  const res = await api.delete(`/shape-up/bet-tasks/${taskId}`);
  return res.data;
};

/**
 * Save the team-shared order of a bet's scratchpad tasks.
 * @param {string} betId
 * @param {string[]} orderedTaskIds - task ids in the chosen order
 */
export const setBetTaskOrder = async (betId, orderedTaskIds) => {
  const res = await api.put(`/shape-up/bets/${betId}/task-order`, { orderedTaskIds });
  return res.data;
};

/**
 * Add a todo by creating a Jira subtask under the scope's issue
 * @param {string} scopeId
 * @param {string} summary
 * @param {string} [fromBetTaskId] - set when a scratchpad task is graduating, so
 *   the server can move that task's images onto whatever the todo becomes: a
 *   Jira attachment on the new subtask, or the manual todo itself with Jira off.
 */
export const addScopeTodo = async (scopeId, summary, fromBetTaskId = null) => {
  const res = await api.post(`/shape-up/scopes/${scopeId}/todos`, {
    summary,
    ...(fromBetTaskId ? { fromBetTaskId } : {}),
  });
  return res.data;
};

/**
 * Rename a todo or check it off, writing straight through to Jira
 * @param {string} scopeId
 * @param {string} todoKey
 * @param {Object} updates - { summary, done }
 */
export const updateScopeTodo = async (scopeId, todoKey, updates) => {
  const res = await api.patch(`/shape-up/scopes/${scopeId}/todos/${todoKey}`, updates);
  return res.data;
};

/**
 * Hand a todo to another scope of the same bet. This reparents the Jira
 * subtask, so it keeps its key, comments, and history.
 * @param {string} scopeId scope the todo is leaving
 * @param {string} todoKey Jira key of the subtask
 * @param {string} targetScopeId scope the todo is joining
 */
export const moveScopeTodo = async (scopeId, todoKey, targetScopeId) => {
  const res = await api.post(`/shape-up/scopes/${scopeId}/todos/${todoKey}/move`, { targetScopeId });
  return res.data;
};

/**
 * Return a todo to the bet's scratchpad: the subtask (Jira) or ScopeTodo
 * (manual) is deleted and a new scratchpad task is created from its summary.
 * @param {string} scopeId scope the todo belongs to
 * @param {string} todoKey Jira key or ScopeTodo id
 * @returns {Promise<object>} the created scratchpad task
 */
export const returnTodoToScratchpad = async (scopeId, todoKey) => {
  const res = await api.post(`/shape-up/scopes/${scopeId}/todos/${todoKey}/to-scratchpad`);
  return res.data;
};

/**
 * Assign a bet member to a todo, or unassign with userId null. Writes through to
 * Jira when the todo is a subtask; stored locally for a manual todo.
 * @param {string} scopeId
 * @param {string} todoKey Jira key or ScopeTodo id
 * @param {string|null} userId bet member's userId, or null to unassign
 */
export const assignScopeTodo = async (scopeId, todoKey, userId) => {
  const res = await api.put(`/shape-up/scopes/${scopeId}/todos/${todoKey}/assignee`, { userId });
  return res.data;
};

/**
 * Assign a bet member to a scope, or unassign with userId null. Writes through to
 * Jira when the scope is a Jira issue; stored locally for a manual scope.
 * @param {string} scopeId
 * @param {string|null} userId bet member's userId, or null to unassign
 */
export const assignScope = async (scopeId, userId) => {
  const res = await api.put(`/shape-up/scopes/${scopeId}/assignee`, { userId });
  return res.data;
};

/**
 * How a cycle went: scopes hammered, completed, and meeting the definition of
 * done. Answered from stored data, so it costs no Jira calls.
 * @param {string} cycleId
 */
export const getCycleScoreboard = async (cycleId) => {
  const res = await api.get(`/shape-up/cycles/${cycleId}/scoreboard`);
  return res.data;
};

/**
 * Cross-cycle Shape Up analytics for an organization, optionally narrowed to one
 * team. Trends only complete cycles; in-progress cycles come back separately.
 * @param {Object} filters - { organizationId, teamId }
 */
export const getShapeUpAnalytics = async (filters = {}) => {
  const { data } = await api.get('/shape-up/analytics', {
    params: buildWorkspaceParams(filters),
  });
  return data;
};

/**
 * In-flight analytics for a single running cycle: progress against appetite, the
 * hill distribution today, and day-by-day burn-up and scope-count trends. Heavy
 * enough (snapshot trails) that it is fetched by id for the one active cycle
 * rather than folded into the cross-cycle roll-up.
 * @param {string} cycleId
 */
export const getActiveCycleAnalytics = async (cycleId) => {
  const res = await api.get(`/shape-up/cycles/${cycleId}/active-analytics`);
  return res.data;
};

/**
 * Tick or untick one definition-of-done item on a scope. Jira is told in a
 * comment once the whole checklist is satisfied.
 * @param {string} scopeId
 * @param {string} itemId
 * @param {boolean} checked
 */
export const updateScopeDod = async (scopeId, itemId, checked) => {
  const res = await api.put(`/shape-up/scopes/${scopeId}/dod`, { itemId, checked });
  return res.data;
};

/**
 * Get all PRs awaiting the current user's review across all bets.
 */
export const getMyReviews = async () => {
  const res = await api.get('/shape-up/my-reviews');
  return res.data;
};

const shapeUpApi = {
  getShapeUpSettings,
  updateShapeUpSettings,
  getStatusMap,
  getIssueTypeMap,
  getCycles,
  createCycle,
  updateCycle,
  deleteCycle,
  getShapeUpPeople,
  getShapeUpTags,
  getBetTagSuggestions,
  getTagDetail,
  getCycleRoster,
  setCycleRoster,
  copyCycleRoster,
  getBetTeam,
  setBetTeam,
  createBetSlackChannel,
  archiveCycleSlackChannels,
  getCycleHill,
  getBetHill,
  getBetHistory,
  getScopeCoverage,
  createBet,
  updateBet,
  deleteBet,
  syncBetScopes,
  syncCycleScopes,
  getShapeUpSyncStatus,
  addScope,
  updateScope,
  removeScope,
  createScopeIssue,
  getScopeTodos,
  getBetTodos,
  getBetTasks,
  addBetTask,
  updateBetTask,
  deleteBetTask,
  setBetTaskOrder,
  addScopeTodo,
  updateScopeTodo,
  moveScopeTodo,
  returnTodoToScratchpad,
  assignScopeTodo,
  assignScope,
  getCycleScoreboard,
  getShapeUpAnalytics,
  getActiveCycleAnalytics,
  getPitches,
  createPitch,
  updatePitch,
  deletePitch,
  betOnPitch,
  closeCycle,
  reopenCycle,
  rebet,
  updateScopeDod,
  getMyReviews
};

export default shapeUpApi;
